import React, { Fragment } from 'react';
import Helmet from 'react-helmet';
import { useTranslation } from 'react-i18next';
import Container from '@careers-wales/react-components/dist/components/layout/Container/Container.js';
import Row from '@careers-wales/react-components/dist/components/layout/Row/Row.js';
import HeroSearch from '@careers-wales/react-components/dist/components/banners/HeroSearch/HeroSearch.js';
import SearchForm from '../../components/SearchForm';
import Sanitise from '@careers-wales/react-components/dist/components/utilities/Sanitise/Sanitise.js';
import Counter from '@careers-wales/react-components/dist/components/results/Counter/Counter.js';
import Pagination from '@careers-wales/react-components/dist/components/results/Pagination/Pagination.js';
import StackedCard from '@careers-wales/react-components/dist/components/cards/StackedCard/StackedCard.js';
import MetaBodyList from '@careers-wales/react-components/dist/components/results/Meta/MetaBodyList.js';
import MetaMore from '@careers-wales/react-components/dist/components/results/Meta/MetaMore.js';
import Notice from '@careers-wales/react-components/dist/components/misc/Notice/Notice.js';
import ScrollIntoView from '@careers-wales/react-components/dist/components/utilities/ScrollIntoView/ScrollIntoView';
import { route } from '../../routes';
import config from '../../config';

function LandingView({ settings, events, from, setFrom, setTo, to, page, setPage, total }) {
  const { t } = useTranslation();

  return (
    <>
      <Helmet>
        <title>{t('view.landing.title')}</title>
      </Helmet>

      <ScrollIntoView trigger={page} shouldRunOnInitialRender={false} />

      <Container>
        <h1>{t('view.landing.title')}</h1>
        <Sanitise string={settings.introduction} />
      </Container>

      <HeroSearch classes={['hero-search--events']}>
        <SearchForm setFrom={setFrom} setTo={setTo} />
      </HeroSearch>

      <Container>
        <Sanitise string={settings.announcements} />

        {from === '' && to === '' && events.length === 0 && (
          <Notice type={'info-icon'}>{t('view.landing.noResultsMessage.' + config.domain)}</Notice>
        )}

        {events.length > 0 && (
          <Counter
            value={total}
            label={events.length === 1 ? t('styleguide:forms.result') : t('styleguide:forms.results')}
          >
            {from && to && (
              <span>
                {events.length === 1 ? t('misc.resultFrom') : t('misc.resultsFrom')}&nbsp;
                <strong>'{from}'</strong>&nbsp;
                {t('misc.to')}&nbsp;
                <strong>'{to}'</strong>
              </span>
            )}
            {!from && !to && (
              <span>{events.length === 1 ? t('styleguide:forms.result') : t('styleguide:forms.results')}</span>
            )}
          </Counter>
        )}

        {(from !== '' || to !== '') && events.length === 0 && <Sanitise string={t('view.landing.noResults')} />}
      </Container>

      <Container>
        <Row>
          {events.map((event, index) => {
            return (
              <StackedCard
                colour="gel-white"
                key={index}
                title={event.title}
                linkTitle={event.title}
                href={event.localised ? route('detail', { id: event.slug.substring(1) }) : event.path}
                alt={event.title}
                source={event.image}
                meta={
                  <>
                    {event.dates.length > 0 && (
                      <MetaBodyList
                        colon={false}
                        label={
                          <Fragment>
                            {event.dates.length > 2 ? (
                              <>
                                {' '}
                                {event.dates[0]}{' '}
                                <MetaMore>
                                  + {event.dates.length - 1} {t('view.detail.otherDates')}
                                </MetaMore>
                              </>
                            ) : (
                              <>
                                {' '}
                                {event.dates.length === 2
                                  ? event.dates[0] + ' ' + t('styleguide:and') + ' ' + event.dates[1]
                                  : event.dates[0]}{' '}
                              </>
                            )}
                          </Fragment>
                        }
                        value={''}
                      />
                    )}

                    {event.locations.length > 0 && (
                      <MetaBodyList
                        colon={false}
                        label={
                          <Fragment>
                            {event.locations.length > 2 ? (
                              <>
                                {' '}
                                {event.locations[0]}{' '}
                                <MetaMore>
                                  + {event.locations.length - 1} {t('view.detail.otherLocations')}
                                </MetaMore>
                              </>
                            ) : (
                              <>
                                {' '}
                                {event.locations.length === 2
                                  ? event.locations[0] + ' ' + t('styleguide:and') + ' ' + event.locations[1]
                                  : event.locations[0]}{' '}
                              </>
                            )}
                          </Fragment>
                        }
                        value={''}
                      />
                    )}

                    {event.aimed_at.length ? (
                      <MetaBodyList label={t('view.detail.aimedAt')} value={event.aimed_at.join(', ')} />
                    ) : (
                      <>
                        {event.age.length > 0 && (
                          <MetaBodyList label={t('view.detail.age')} value={event.age.join(', ')} />
                        )}
                        {event.year_groups.length > 0 && (
                          <MetaBodyList label={t('view.detail.year_groups')} value={event.year_groups.join(', ')} />
                        )}
                        {event.status.length > 0 && (
                          <MetaBodyList label={t('view.detail.status')} value={event.status.join(', ')} />
                        )}
                        {event.focus.length > 0 && (
                          <MetaBodyList label={t('view.detail.focus')} value={event.focus.join(', ')} />
                        )}
                      </>
                    )}
                  </>
                }
              >
                <Sanitise string={event.description} />
              </StackedCard>
            );
          })}
        </Row>
        <Pagination total={Math.ceil(total / 10)} current={page} onPageChange={setPage} />
      </Container>
    </>
  );
}

export default LandingView;
